import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type {
  CompanyDetail,
  CreditsafeCandidate,
  CreditsafeSearchResponse,
} from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";
import { cn } from "../ui/cn";
import { Field, Input } from "./fields";

/**
 * Find this company at Creditsafe, and link it.
 *
 * The dialog exists because the match cannot be made automatically. A
 * wholesaler's eight depots share a name, a domain and often a postcode; a
 * fuzzy match that picks the wrong one attaches another company's credit limit
 * to a live account, in the one panel a rep trusts without re-checking. So the
 * search is prefilled from what the record already holds, the results carry
 * everything needed to tell two branches apart, and a person chooses.
 *
 * **Linking orders a report, and a report is billed.** That is said on the
 * button rather than buried in a tooltip: somebody clicking through candidates
 * to see which is right should know which click spends money.
 */
export function CreditsafeLinkDialog({
  company,
  onClose,
}: {
  company: CompanyDetail;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  /*
   * Prefilled from the record, because the fastest search is the one already
   * typed -- but only ever with ONE of Connect's criteria sets.
   *
   * Connect does not take an arbitrary bag of criteria. `GET
   * /companies/searchcriteria?countries=GB` reports five *mutually exclusive*
   * sets: `id`, `safeNo`, `regNo` and `vatNo` each on their own, or a
   * combination set of name, address, phone, status and type. Mixing sets is
   * rejected outright -- "Specified criteria combination is not allowed" --
   * which is a 400 rather than an empty result, so it reads as the CRM being
   * broken rather than as a search that needs narrowing.
   *
   * So: the registration number alone when we hold one, since it is a unique
   * key at Creditsafe's end and turns a list of eleven Smiths into one hit; the
   * name otherwise. `#criteria` below is what guarantees the two never travel
   * together, whatever somebody types.
   *
   * There is no website field, and that is not an omission: `website` is in
   * none of GB's criteria sets, so searching on it fails even on its own.
   */
  const [form, setForm] = useState({
    name: company.companyNumber ? "" : company.name,
    regNo: company.companyNumber ?? "",
    postCode: "",
  });

  /* Held separately from the form so typing does not re-run the search on every
     keystroke against a paid third party's rate limit. */
  const [criteria, setCriteria] = useState<SearchCriteria | null>(null);

  const status = useQuery({
    queryKey: queryKeys.crmCreditsafeStatus,
    queryFn: crmApi.creditsafeStatus,
    staleTime: 5 * 60_000,
  });

  const results = useQuery({
    queryKey: queryKeys.crmCreditsafeSearch(criteria ?? {}),
    queryFn: () => crmApi.creditsafeSearch(criteria!),
    enabled: criteria !== null,
    /* Creditsafe's answer about a company does not change while a dialog is
       open, and re-running it on a refocus is a request nobody asked for. */
    staleTime: 5 * 60_000,
    retry: false,
  });

  const link = useMutation({
    mutationFn: (candidate: CreditsafeCandidate) =>
      crmApi.linkCredit(company.id, {
        connectId: candidate.connectId,
        safeNo: candidate.safeNo,
      }),
    onSuccess: () => {
      /* The whole company: the figures live on the record, the report lives in
         its own query, and the timeline has just gained an audit entry. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompany(company.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCredit(company.id) });
      void queryClient.invalidateQueries({ queryKey: ["crm", "timeline", company.id] });
      onClose();
    },
  });

  const searchable = toCriteria(form);
  const usingRegNo = form.regNo.trim() !== "";

  return (
    <Overlay onClose={onClose}>
      <h2 className="text-sm font-semibold text-content">
        Link {company.name} to Creditsafe
      </h2>
      <p className="mt-1 text-[11px] text-muted">
        Find their record at Creditsafe, then link it to pull their credit file
        onto this account.
      </p>

      {status.data && !status.data.enabled ? (
        <p className="mt-4 rounded-md bg-surface-2 px-3 py-2 text-xs text-muted-on-tint">
          No credit bureau is connected on this server. An admin needs to add the
          Creditsafe credentials before anyone can search.
        </p>
      ) : (
        <>
          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (searchable) setCriteria(searchable);
            }}
          >
            {/*
              The registration number first, and on its own row, because it is
              the criterion that wins: Connect searches on it *alone*, so
              anything typed below is ignored while it has a value. Said in the
              hint rather than by disabling the other two -- a field greyed out
              with no explanation invites the wrong guess about which one to
              clear.
            */}
            <Field
              label="Registration number"
              hint={
                usingRegNo
                  ? "Searched on its own — Creditsafe takes a registration number by itself. Clear it to search by name instead."
                  : "A Companies House CRN. The most precise way to search."
              }
            >
              <Input
                value={form.regNo}
                onChange={(regNo) => setForm({ ...form, regNo })}
                placeholder="03836192"
                autoFocus={form.regNo !== ""}
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="Company name"
                hint={usingRegNo ? "Not used while a registration number is set" : undefined}
              >
                <Input
                  value={form.name}
                  onChange={(name) => setForm({ ...form, name })}
                  placeholder="Acme Trading Ltd"
                  autoFocus={form.regNo === ""}
                />
              </Field>
              <Field
                label="Postcode"
                hint={
                  usingRegNo
                    ? "Not used while a registration number is set"
                    : "Narrows a common name to one branch"
                }
              >
                <Input
                  value={form.postCode}
                  onChange={(postCode) => setForm({ ...form, postCode })}
                  placeholder="S60 1AB"
                />
              </Field>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" size="sm" disabled={!searchable}>
                {results.isFetching ? "Searching…" : "Search Creditsafe"}
              </Button>
              {/* Said next to the button that is free, so the contrast with the
                  one below that is not lands before anybody presses either. */}
              <span className="text-[11px] text-faint">
                Searching is free. Linking orders a credit report.
              </span>
            </div>
          </form>

          {criteria === null ? null : (
            <Results
              data={results.data}
              isFetching={results.isFetching}
              error={results.isError ? results.error : null}
              onLink={(candidate) => link.mutate(candidate)}
              linkingId={link.isPending ? link.variables.connectId : undefined}
              currentConnectId={company.creditsafeId}
            />
          )}

          {link.isError ? (
            <p className="mt-3 text-[11px] text-sla-breached">
              {link.error instanceof ApiError
                ? link.error.message
                : "Could not link that company."}
            </p>
          ) : null}
        </>
      )}
    </Overlay>
  );
}

/** Exactly one of Connect's criteria sets. Never a mixture of two. */
type SearchCriteria =
  | { regNo: string }
  | { name: string; postCode?: string };

/**
 * The form, reduced to one valid criteria set -- or null when there is nothing
 * to search on.
 *
 * This is the single place the rule lives, rather than three fields politely
 * agreeing to stay out of each other's way. Connect's GB sets are mutually
 * exclusive (`GET /companies/searchcriteria?countries=GB`), and mixing them is
 * a flat 400 -- "Specified criteria combination is not allowed" -- which surfaces
 * as the CRM looking broken rather than as a search that needs narrowing.
 *
 * A registration number wins outright when present, because it is a unique key:
 * there is no sense in which adding a postcode to it could improve the answer,
 * so dropping the rest costs nothing and makes the request always valid.
 *
 * A postcode with no name is deliberately not searchable on its own. Connect
 * would accept it -- address fields are in the combination set -- but "every
 * company in S60" is a `TooManyResults` after a round trip, and the button
 * being disabled says so before it is pressed.
 */
function toCriteria(form: {
  name: string;
  regNo: string;
  postCode: string;
}): SearchCriteria | null {
  const regNo = form.regNo.trim();
  if (regNo) return { regNo };

  const name = form.name.trim();
  if (!name) return null;

  const postCode = form.postCode.trim();
  return postCode ? { name, postCode } : { name };
}

function Results({
  data,
  isFetching,
  error,
  onLink,
  linkingId,
  currentConnectId,
}: {
  data: CreditsafeSearchResponse | undefined;
  isFetching: boolean;
  error: unknown;
  onLink: (candidate: CreditsafeCandidate) => void;
  linkingId: string | undefined;
  currentConnectId: string | null;
}) {
  if (error) {
    return (
      <p className="mt-4 text-xs text-sla-breached">
        {error instanceof ApiError ? error.message : "Could not search Creditsafe."}
      </p>
    );
  }

  if (!data) {
    return isFetching ? (
      <p className="mt-4 text-xs text-faint">Searching Creditsafe…</p>
    ) : null;
  }

  if (data.matches.length === 0) {
    return (
      <p className="mt-4 rounded-md bg-surface-2 px-3 py-2 text-xs text-muted-on-tint">
        {/* Creditsafe's own words when it has them. "TooManyResults" and
            "NoResults" are different problems with different fixes, and only
            the searcher can act on the difference. */}
        {data.message ?? "Creditsafe has no company matching that."}
      </p>
    );
  }

  return (
    <div className="mt-4">
      <p className="mb-2 text-[11px] text-faint">
        {data.totalSize > data.matches.length
          ? `Showing ${data.matches.length} of ${data.totalSize} — add a postcode to narrow it down.`
          : `${data.matches.length} ${data.matches.length === 1 ? "match" : "matches"}`}
      </p>

      <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
        {data.matches.map((candidate) => (
          <CandidateRow
            key={candidate.connectId}
            candidate={candidate}
            isCurrent={candidate.connectId === currentConnectId}
            isLinking={candidate.connectId === linkingId}
            disabled={linkingId !== undefined}
            onLink={() => onLink(candidate)}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * One candidate.
 *
 * Everything shown here is here to tell two branches of one group apart:
 * address, registration number, office type and status. A row with only a name
 * on it makes the choice a coin flip, which is the failure this whole dialog
 * exists to avoid.
 */
function CandidateRow({
  candidate,
  isCurrent,
  isLinking,
  disabled,
  onLink,
}: {
  candidate: CreditsafeCandidate;
  isCurrent: boolean;
  isLinking: boolean;
  disabled: boolean;
  onLink: () => void;
}) {
  const inactive =
    candidate.status !== null && candidate.status.toLowerCase() !== "active";

  return (
    <li className="flex items-start gap-3 rounded-md border border-line px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs font-medium text-content">{candidate.name}</span>
          {candidate.status ? (
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px]",
                inactive
                  ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
                  : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
              )}
            >
              {candidate.status}
            </span>
          ) : null}
          {candidate.officeType ? (
            <span className="text-[10px] text-faint">{candidate.officeType}</span>
          ) : null}
        </div>

        {candidate.address.simpleValue ? (
          <p className="mt-0.5 truncate text-[11px] text-muted">
            {candidate.address.simpleValue}
          </p>
        ) : null}

        <p className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-faint">
          {candidate.regNo ? <span>Reg {candidate.regNo}</span> : null}
          {candidate.vatNo ? <span>VAT {candidate.vatNo}</span> : null}
          {candidate.activity ? <span>{candidate.activity}</span> : null}
          {candidate.dateOfLatestAccounts ? (
            <span>Accounts to {candidate.dateOfLatestAccounts}</span>
          ) : null}
        </p>

        {/*
          Said rather than hidden. One Creditsafe record legitimately backs a
          group and its trading branch, so this is information, not a refusal --
          but linking a third company to the same record is usually a mistake
          and worth seeing before it is made.
        */}
        {candidate.linkedTo && !isCurrent ? (
          <p className="mt-1 text-[11px] text-sla-warning">
            Already linked to{" "}
            <Link
              to={`/crm/companies/${candidate.linkedTo.id}`}
              className="underline hover:text-brand-600"
            >
              {candidate.linkedTo.name}
            </Link>
          </p>
        ) : null}
      </div>

      {isCurrent ? (
        <span className="shrink-0 self-center text-[11px] text-faint">Linked</span>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className="shrink-0 self-center"
          disabled={disabled}
          title="Links this company and orders a credit report — this is billed"
          onClick={onLink}
        >
          {isLinking ? "Ordering…" : "Link & order report"}
        </Button>
      )}
    </li>
  );
}
